import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { PensionForecastTable } from "../components/summaryPensionTable";
import { Title, Text, Button, Paper } from "@mantine/core";
import { useGetCalculationById } from "../hooks/api/";
import { useAdviceInputStore, useUpdateCustomerData } from "../hooks/ui";
import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { isFullCredentials } from "../hooks/ui/customerDataStore";
import { useGetLockedAdvice } from "../hooks/api/useGetLockedAdvice";
import ErrorComponent from "../components/errorComponent";
import { LoaderComponent } from "../components/loaderComponent";
import { FooterButtons } from "../components/footerButtons";
import PoaExpiredBanner from "../components/poaValidation";

export const Route = createFileRoute("/pension-forecast")({
  component: PensionForecastPage,
});

function PensionForecastPage() {
  const [hasLockedAdvice, setHasLockedAdvice] = useState(false);
  const navigate = useNavigate();
  const { customerData } = useUpdateCustomerData();

  const { data, isLoading, isError, isSuccess } = useGetCalculationById({
    credentials: customerData.credentials,
    enabled: isFullCredentials(customerData.credentials),
  });

  const queryClient = useQueryClient();

  const { updateRetirementAge, updateRiskPreference } = useAdviceInputStore(
    (state) => state,
  );

  const { data: adviceState } = useGetLockedAdvice({
    credentials: customerData.credentials,
    enabled: isFullCredentials(customerData.credentials),
  });

  useEffect(() => {
    setHasLockedAdvice(adviceState?.isLocked);
  }, [adviceState]);

  useEffect(() => {
    if (!isLoading && isSuccess && data) {
      updateRetirementAge(data.parameters.zAge);
      updateRiskPreference(data.parameters.riskPreference);
    }
  }, [isSuccess, isLoading, data, updateRetirementAge, updateRiskPreference]);

  if (isError) {
    return <ErrorComponent />;
  }

  if (isLoading) {
    return <LoaderComponent />;
  }

  return (
    <>
      <PoaExpiredBanner />
      {isSuccess && (
        <>
          {" "}
          {hasLockedAdvice ? (
            <Paper
              p={"lg"}
              radius={"lg"}
              mb={"xl"}
              mt={"xl"}
              bg={"var(--wallet-color-masterbrand-accent-yellow)"}
            >
              <Title
                order={1}
                mb={"sm"}
                ff={"var(--wallet-font-family-sambla-sans)"}
                fz={"1.3rem"}
              >
                Du har ett aktivt råd som väntar på signering
              </Title>
              <Text mb={"sm"}>
                Här nedan kan du se de parametrar som ligger till grund för
                rådet som du gick igenom med din rådgivare. Gå vidare för att se
                en sammanställning och signera rådet. Vänligen kontakta oss om
                du vill göra några ändringar.
              </Text>
            </Paper>
          ) : (
            <>
              <Title
                ff={"var(--wallet-font-family-sambla-sans)"}
                mt={"xl"}
                order={1}
              >
                Hur vill du att din pension ska se ut?
              </Title>
              <Text mb={"xl"}>
                Dra i reglagen för att simulera prognosen efter dina önskemål.
                När du känner dig nöjd tar vi fram skräddarsydda råd så att du
                kan nå dit.
              </Text>
            </>
          )}
          <PensionForecastTable hasLockedAdvice={hasLockedAdvice} />
          <Button
            fullWidth
            mt="lg"
            mb="lg"
            bg={
              hasLockedAdvice
                ? "var(--wallet-color-neutrals-gray-200)"
                : "white"
            }
            variant="outline"
            color={
              hasLockedAdvice
                ? "var(--wallet-color-neutrals-gray-500)"
                : "var(--wallet-color-sambla-green-shade-700)"
            }
            onClick={() => {
              queryClient.invalidateQueries({
                queryKey: ["get-calculation-by-id"],
              });
              queryClient.invalidateQueries({
                queryKey: ["get-locked-advice-by-user-id"],
              });
            }}
          >
            Uppdatera
          </Button>
          <FooterButtons>
            <Button component={Link} to="/pension-overview" variant="default">
              Tillbaka
            </Button>
            <Button
              disabled={!data?.transferAllocation}
              onClick={() => navigate({ to: "/advice-summary" })}
            >
              Se råd
            </Button>
          </FooterButtons>
        </>
      )}
    </>
  );
}
